import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./modal.css";
import H3 from "../typography/H3";
import close from "../../assets/close.svg";
import SimpleButton from "../buttons/Simple-button";
import TextButton from "../buttons/text-button";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";

interface RightSideModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  buttonlabel?: string;
  children: React.ReactNode;
  formId?: string;
  maxWidth?: number;
  isDirty?: boolean;
  isFooterHidden?: boolean;
  isSubmitDisabled?: boolean;
  className?: string;
  customFooter?: React.ReactNode;
}

const RightSideModal: React.FC<RightSideModalProps> = ({
  isOpen,
  onClose,
  title,
  buttonlabel = "",
  children,
  formId,
  maxWidth = 450,
  isDirty = false,
  isFooterHidden = false,
  isSubmitDisabled = false,
  className = "",
  customFooter,
}) => {
  const [isUnsavedChangesOpen, setIsUnsavedChangesOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const handleClose = () => {
    if (isDirty) {
      setIsUnsavedChangesOpen(true); // Show popup if form is dirty
    } else {
      onClose(); // Close modal directly if no changes
    }
  };

  // Prevent SSR errors if document is undefined
  if (typeof document === "undefined") return null;

  return createPortal(
    <>
      {isOpen && (
        <div className="modal-backdrop-shadow" onClick={handleClose} />
      )}
      <div
        className={`right-side-modal ${isOpen ? "open" : ""}`}
        style={{ maxWidth: maxWidth }}
      >
        <div className="d-flex flex-column h-100">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center px-4 pt-4">
            <H3 text={title} />
            <img
              className="close-icon"
              src={close}
              alt="Close Icon"
              onClick={handleClose}
            />
          </div>

          {/* Body */}
          <div className={`${className} flex-grow-1 right-modal-content`}>
            <div className="right-modal-scroll">{children}</div>
          </div>

          {/* Footer */}
          {!isFooterHidden && (
            <div className="p-3 border-footer-top d-flex justify-content-end gap-2 bg-white">
              {customFooter ? (
                customFooter
              ) : (
                <>
                  <TextButton label={"Cancel"} onClick={handleClose} />

                  <SimpleButton
                    label={buttonlabel}
                    type={formId ? "submit" : "button"}
                    form={formId}
                    className="mx-2"
                    isDisabled={isSubmitDisabled}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmationPopup
        isOpen={isUnsavedChangesOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. If you go back or exit this step, your changes will be lost. Do you want to continue?"
        primaryButtonLabel="Cancel"
        secondaryButtonLabel="Continue Without Saving"
        onPrimaryAction={() => setIsUnsavedChangesOpen(false)}
        onSecondaryAction={() => {
          setIsUnsavedChangesOpen(false);
          onClose();
        }}
        onClose={() => setIsUnsavedChangesOpen(false)}
        isPrimaryAction={false}
      />
    </>,
    document.body,
  );
};

export default RightSideModal;
